import {NgModule} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormErrorListComponent} from './components/form-error-list/form-error-list.component';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {RouterModule} from '@angular/router';
import {EnumValuesPipe} from './pipes/enum-values.pipe';
import {ConvertToCountryCodePipe} from './pipes/convert-to-country-code.pipe';
import {ConvertFromCountryCodePipe} from './pipes/convert-from-country-code.pipe';
import {ShineDirective} from './directives/shine.directive';
import {ThousandSuffixesPipe} from './pipes/thousand-suffixes.pipe';
import {LocalizedDatePipe} from './pipes/localized-date.pipe';
import {ExciseNumberValidatorDirective} from './validators/excise-number-validator.directive';
import {PhoneNumberValidatorDirective} from './validators/phone-number-validator.directive';
import {GlnValidatorDirective} from './validators/gln-validator.directive';
import {GiaiValidatorDirective} from './validators/giai-validator.directive';
import {FacilityListComponent} from './components/facility-list/facility-list.component';
import {LanguageSelectionComponent} from './components/language-selection/language-selection.component';
import {StopPropagationDirective} from './directives/stop-propagation.directive';
import {SamePasswordValidatorDirective} from './validators/same-password-validator.directive';
import {BarChartComponent} from './components/bar-chart/bar-chart.component';
import {GreaterTimeValidatorDirective} from './validators/greater-time-validator.directive';
import {LocalizedDateTimePipe} from './pipes/localized-date-time.pipe';
import {TranslateLanguageNamePipe} from './pipes/translate-language-name.pipe';
import {ChangeLogChangeViewComponent} from './components/change-log-change-view/change-log-change-view.component';
import {ChangeLogTableComponent} from './components/change-log-table/change-log-table.component';
import {RecaptchaModule} from 'ng-recaptcha';
import {PasswordStrengthValidatorDirective} from './validators/password-strength-validator.directive';
import {NumberValidatorDirective} from './validators/number-validator.directive';
import {VatValidatorDirective} from './validators/vat-validator.directive';
import {EmailValidatorDirective} from './validators/email-validator.directive';
import {MatSelectModule} from '@angular/material/select';
import {NgxMatSelectSearchModule} from 'ngx-mat-select-search';
import {MatSelectSearchComponent} from './components/mat-select-search/mat-select-search.component';
import {MachineListComponent} from './components/machine-list/machine-list.component';
import {StringValidatorDirective} from './validators/string-validator.directive';
import {ReasonValidatorDirective} from './validators/reason-validator.directive';
import {FormattedNumberPipe} from './pipes/formatted-number.pipe';
import {LeafletModule} from '@asymmetrik/ngx-leaflet';
import {GtinValidatorDirective} from './validators/gtin-validator.directive';
import {EntityChangeViewComponent} from './components/entity-change-view/entity-change-view.component';
import {DefaultValuePipe} from './pipes/default-value.pipe';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatDialogModule} from '@angular/material/dialog';
import {MatButtonModule} from '@angular/material/button';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatInputModule} from '@angular/material/input';
import {EconomicOperatorSelectionComponent} from './components/economic-operator-selection/economic-operator-selection.component';
import {FacilitySelectionComponent} from './components/facility-selection/facility-selection.component';
import {MachineSelectionComponent} from './components/machine-selection/machine-selection.component';
import {ProductSelectionComponent} from './components/product-selection/product-selection.component';
import {InfoModalComponent} from './dialogs/info-modal/info-modal.component';
import {MapModalComponent} from './dialogs/map-modal/map-modal.component';
import {ConfirmationModalComponent} from './dialogs/confirmation-modal/confirmation-modal.component';
import {DataProtectionPolicyModalComponent} from './dialogs/data-protection-policy-modal/data-protection-policy-modal.component';
import {ForgotPasswordModalComponent} from './dialogs/forgot-password-modal/forgot-password-modal.component';
import {SessionExpirationModalComponent} from './dialogs/session-expiration-modal/session-expiration-modal.component';
import {ChangeLogModalComponent} from './dialogs/change-log-modal/change-log-modal.component';
import {MatRadioModule} from '@angular/material/radio';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatCheckboxModule} from '@angular/material/checkbox';
import { MatTableModule } from '@angular/material/table';
import {FormGroupComponent} from './components/form-group/form-group.component';
import {ProductsListComponent} from 'src/app/modules/products/components/products-list/products-list.component';
import {CustomerProductIdValidatorDirective} from './validators/customer-product-id-validator.directive';
import {MessagesModalComponent} from './dialogs/messages-modal/messages-modal.component';
import {MapComponent} from './components/map/map.component'
import { RelatedEconomicOperatorsComponent } from './components/related-economic-operators/related-economic-operators.component';
import { RevenueDashboardComponent } from './components/revenue-dashboard/revenue-dashboard.component';
import { DashboardFilterBarComponent } from './components/dashboard-filter-bar/dashboard-filter-bar.component';
import { GoogleMapsModule } from '@angular/google-maps';
import { ProductionVolumeComponent } from './components/production-volume/production-volume.component';
import { FacilityNameUniqueValidatorDirective } from './validators/facility-name-unique-validator.directive';
import { ProductNameUniqueValidatorDirective } from './validators/product-name-unique-validator.directive';
import { MachineNameUniqueValidatorDirective } from './validators/machine-name-unique-validator.directive';
import { AtLeastOneContactValidator } from './validators/AtLeastOneContactValidator.directive';
import { TreeVisualizationComponent } from './components/tree-visualization/tree-visualization.component';
import { MatChipsModule } from '@angular/material/chips';
import { MatBadgeModule } from '@angular/material/badge';
import { TaxLogModalComponent } from './dialogs/tax-log-modal/tax-log-modal.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTreeModule } from '@angular/material/tree';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ProductImageModalComponent } from './dialogs/product-image-modal/product-image-modal.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatMenuModule } from '@angular/material/menu';
import { OrdersFormattedNumberPipe } from './pipes/orders-formatted-number.pipe';
import { MatSortModule } from '@angular/material/sort';
import { ProductTypeTagsComponent } from './components/product-type-tags/product-type-tags.component';
import { MatDividerModule } from '@angular/material/divider';
import { ApprovalInfoMessageComponent } from './components/approval-info-message/approval-info-message.component';
import {VerticalTabsComponent} from './components/vertical-tabs/vertical-tabs.component';
import {PageHeaderComponent} from './components/page-header/page-header.component';
import {LoadingOverlayComponent} from './components/loading-overlay/loading-overlay.component';
import {EmptyStateComponent} from './components/empty-state/empty-state.component';
import {A11yModule} from '@angular/cdk/a11y';
import {CurrencySymbolPipe} from './pipes/currency-symbol.pipe';
import { CurrencyConvertPipe } from './pipes/currency-convert.pipe';
import { FormatEntityNamePipe } from './pipes/format-entity-name.pipe';

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    RouterModule,
    RecaptchaModule,
    LeafletModule,
    MatSelectModule,
    MatIconModule,
    MatTooltipModule,
    MatDialogModule,
    MatButtonModule,
    MatExpansionModule,
    MatSlideToggleModule,
    NgxMatSelectSearchModule,
    MatTableModule,
    GoogleMapsModule,
    MatDatepickerModule,
    ReactiveFormsModule,
    MatChipsModule,
    MatBadgeModule,
    MatProgressSpinnerModule,
    MatTreeModule,
    MatProgressBarModule,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    MatPaginatorModule,
    MatMenuModule,
    MatSortModule,
    MatDividerModule,
    A11yModule,
    
],
  providers: [
    ThousandSuffixesPipe,
  ],
  declarations: [
    FormErrorListComponent,
    InfoModalComponent,
    EnumValuesPipe,
    ConvertToCountryCodePipe,
    ConvertFromCountryCodePipe,
    FormGroupComponent,
    ShineDirective,
    ThousandSuffixesPipe,
    LocalizedDatePipe,
    ExciseNumberValidatorDirective,
    PhoneNumberValidatorDirective,
    GlnValidatorDirective,
    FacilityNameUniqueValidatorDirective,
    MachineNameUniqueValidatorDirective,
    AtLeastOneContactValidator,
    GiaiValidatorDirective,
    FacilityListComponent,
    LanguageSelectionComponent,
    StopPropagationDirective,
    SamePasswordValidatorDirective,
    BarChartComponent,
    GreaterTimeValidatorDirective,
    LocalizedDateTimePipe,
    TranslateLanguageNamePipe,
    ChangeLogModalComponent,
    ChangeLogChangeViewComponent,
    ChangeLogTableComponent,
    PasswordStrengthValidatorDirective,
    NumberValidatorDirective,
    EmailValidatorDirective,
    VatValidatorDirective,
    MatSelectSearchComponent,
    MachineListComponent,
    StringValidatorDirective,
    ReasonValidatorDirective,
    FormattedNumberPipe,
    OrdersFormattedNumberPipe,
    MapModalComponent,
    GtinValidatorDirective,
    EntityChangeViewComponent,
    DefaultValuePipe,
    EconomicOperatorSelectionComponent,
    FacilitySelectionComponent,
    MachineSelectionComponent,
    ProductSelectionComponent,
    ConfirmationModalComponent,
    DataProtectionPolicyModalComponent,
    ForgotPasswordModalComponent,
    SessionExpirationModalComponent,
    ProductsListComponent,
    CustomerProductIdValidatorDirective,
    MessagesModalComponent,
    MapComponent,
    RelatedEconomicOperatorsComponent,
    RevenueDashboardComponent,
    DashboardFilterBarComponent,
    ProductionVolumeComponent,
    ProductNameUniqueValidatorDirective,
    TreeVisualizationComponent,
    TaxLogModalComponent,
    ProductImageModalComponent,
    ProductTypeTagsComponent,
    ApprovalInfoMessageComponent,
    VerticalTabsComponent,
    PageHeaderComponent,
    LoadingOverlayComponent,
    EmptyStateComponent,
    CurrencySymbolPipe,
    CurrencyConvertPipe,
    FormatEntityNamePipe
  ],
  exports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    RouterModule,
    FormErrorListComponent,
    InfoModalComponent,
    EnumValuesPipe,
    ConvertToCountryCodePipe,
    ConvertFromCountryCodePipe,
    FormGroupComponent,
    ShineDirective,
    ThousandSuffixesPipe,
    LocalizedDatePipe,
    ExciseNumberValidatorDirective,
    PhoneNumberValidatorDirective,
    GlnValidatorDirective,
    FacilityNameUniqueValidatorDirective,
    MachineNameUniqueValidatorDirective,
    AtLeastOneContactValidator,
    GiaiValidatorDirective,
    FacilityListComponent,
    LanguageSelectionComponent,
    StopPropagationDirective,
    SamePasswordValidatorDirective,
    BarChartComponent,
    GreaterTimeValidatorDirective,
    LocalizedDateTimePipe,
    TranslateLanguageNamePipe,
    RecaptchaModule,
    ChangeLogChangeViewComponent,
    ChangeLogTableComponent,
    PasswordStrengthValidatorDirective,
    NumberValidatorDirective,
    EmailValidatorDirective,
    VatValidatorDirective,
    EmailValidatorDirective,
    MatSelectModule,
    MatSelectSearchComponent,
    MachineListComponent,
    StringValidatorDirective,
    ReasonValidatorDirective,
    FormattedNumberPipe,
    OrdersFormattedNumberPipe,
    MapModalComponent,
    LeafletModule,
    GtinValidatorDirective,
    EntityChangeViewComponent,
    DefaultValuePipe,
    MatExpansionModule,
    MatIconModule,
    MatTooltipModule,
    MatDialogModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatInputModule,
    EconomicOperatorSelectionComponent,
    FacilitySelectionComponent,
    MachineSelectionComponent,
    ProductSelectionComponent,
    MatRadioModule,
    MatDatepickerModule,
    MatCheckboxModule,
    ProductsListComponent,
    CustomerProductIdValidatorDirective,
    MapComponent,
    RelatedEconomicOperatorsComponent,
    RevenueDashboardComponent,
    DashboardFilterBarComponent,
    ProductionVolumeComponent,
    ProductNameUniqueValidatorDirective,
    TreeVisualizationComponent,
    MatChipsModule,
    MatBadgeModule,
    MatTreeModule,
    MatProgressBarModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    ProductTypeTagsComponent,
    ApprovalInfoMessageComponent,
    VerticalTabsComponent,
    PageHeaderComponent,
    LoadingOverlayComponent,
    EmptyStateComponent,
    A11yModule,
    CurrencySymbolPipe,
    CurrencyConvertPipe,
    FormatEntityNamePipe
  ]
})
export class SharedModule {
}
